<script setup lang="ts">
import { computed, ref, watch, onErrorCaptured } from 'vue';
import { useI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';

type ErrorMode = 'fallback' | 'hide' | 'keep';

const props = defineProps<{
  fallback?: string;
  silent?: boolean;
  mode?: ErrorMode;
  tag?: string;
}>();

const emit = defineEmits<{ error: [err: Error] }>();

const { t } = useI18n();
const fallbackText = computed(() => props.fallback ?? t('core.pages.error.subtitle'));
const mode = computed<ErrorMode>(() => props.mode ?? 'fallback');
const capturedError = ref<Error | null>(null);
let errorReported = false;

watch(
  () => props.silent,
  (isSilent) => {
    if (!isSilent && capturedError.value && !errorReported) {
      errorReported = true;
      Sentry.captureException(capturedError.value);
    }
  },
);

onErrorCaptured((error) => {
  if (!ErrorUtil.isUnexpected(error)) {
    return;
  }

  const normalizedError = error instanceof Error ? error : new Error(String(error));

  capturedError.value = normalizedError;
  emit('error', normalizedError);

  if (!props.silent && !errorReported) {
    errorReported = true;
    Sentry.captureException(error);
  }

  return false;
});

function retry() {
  errorReported = false;
  capturedError.value = null;
}
</script>

<template>
  <component
    :is="tag || 'div'"
    v-show="!capturedError || mode !== 'hide'"
    :class="{ 'eb-passthrough': !tag }"
  >
    <slot v-if="!capturedError || mode === 'keep'" />
    <slot
      v-else-if="mode === 'fallback'"
      name="fallback"
      :error="capturedError"
      :fallback="fallbackText"
      :retry="retry"
    >
      <div class="error-boundary">
        <p>{{ fallbackText }}</p>
        <button
          type="button"
          @click="retry"
        >
          {{ $t('core.common.button.retry') }}
        </button>
      </div>
    </slot>
  </component>
</template>

<style scoped>
.eb-passthrough {
  display: contents;
}

.error-boundary {
  display: grid;
  gap: 12px;
  place-items: center;

  padding: 16px;

  text-align: center;
}
</style>
